/**
 * BAB Line Account Modal — account-level drill-down for one Sum line.
 *
 * Mirrors BabLineDetailModal in the BAB Process App: Actual / Budget / Prior Year with
 * deltas, every column sortable, a company filter that also drives the Total row, and a
 * comment badge per account that includes the comments written on its bookings.
 *
 * Amounts are ALWAYS full EUR here — the report's thousands toggle stops at the overview,
 * otherwise a 3'570 EUR account would read "4".
 */
import { useState, useEffect, useMemo } from 'react';
import { X, Loader2, MessageSquare, HelpCircle, Info, Lock } from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { formatCompanyLabel } from '../../utils/buTheme';
import { formatEur, formatPercent, deltaPct, getDeltaColorClass, applySign } from '../../utils/babFormat';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { BabAccountTransactionModal } from './BabAccountTransactionModal';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import type { BabLineResult, BabLineAccount, BabCommentTarget, BabCommentCount, BabQueryCount, SignMode } from '../../types/babReport';

type SortKey = 'company' | 'account' | 'actualYTD' | 'budgetYTD' | 'deltaBudget'
    | 'deltaBudgetPct' | 'priorYearYTD' | 'deltaPY' | 'deltaPYPct';

const NUMERIC_KEYS: SortKey[] = ['actualYTD', 'budgetYTD', 'deltaBudget', 'deltaBudgetPct', 'priorYearYTD', 'deltaPY', 'deltaPYPct'];

/** Widths in px; the user's own drag wins and outlives the session. `account` is the wide one —
 *  dpt spells out a licence plate in the account name. */
const BADGE_COL_WIDTH = 40;

const COLUMNS: { key: SortKey; label: string; width: number; align: 'left' | 'right' }[] = [
    { key: 'company', label: 'Company', width: 110, align: 'left' },
    { key: 'account', label: 'Account', width: 360, align: 'left' },
    { key: 'actualYTD', label: 'Actual YTD', width: 120, align: 'right' },
    { key: 'budgetYTD', label: 'Budget YTD', width: 120, align: 'right' },
    { key: 'deltaBudget', label: 'Delta Budget', width: 120, align: 'right' },
    { key: 'deltaBudgetPct', label: 'Delta %', width: 85, align: 'right' },
    { key: 'priorYearYTD', label: 'Prior Year', width: 120, align: 'right' },
    { key: 'deltaPY', label: 'Delta PY', width: 120, align: 'right' },
    { key: 'deltaPYPct', label: 'Delta %', width: 85, align: 'right' },
];

/** Same key the page uses when bucketing comments by account. */
export function accountKey(companyCode: string, accountCode: string): string {
    return `${companyCode}:${accountCode}`;
}

interface Props {
    line: BabLineResult;
    year: number;
    month: number;
    apiFilters: { babCompanyCode?: string; buCode?: string; sourceCompany?: string };
    /**
     * Follows the grid's sign toggle. Unlike the amount SCALE (always full EUR down here), the
     * sign has to agree with the row the user clicked — a cost shown negative in the grid and
     * positive one level deeper reads as a bug.
     */
    signMode: SignMode;
    /** Shown next to Company only when the report is not already filtered to one BU. */
    showBuColumn: boolean;
    /**
     * May this user drill from an account into its individual bookings? Per source company.
     * A locked row keeps every figure — those are level-2 data the user is entitled to, and
     * dropping them would silently change the Total row. Only the drill-in goes away.
     * The real gate is server-side; this just avoids clicking into a 403.
     */
    canDrillCompany: (companyCode: string) => boolean;
    commentCounts: Record<string, BabCommentCount>;
    commentedTxRefs: Set<string>;
    onCommentClick: (target: BabCommentTarget) => void;
    /** Rueckfragen on this line's accounts, keyed the same way as commentCounts. */
    queryCounts: Record<string, BabQueryCount>;
    queriedTxRefs: Set<string>;
    onQueryClick: (target: BabCommentTarget) => void;
    /** Account to open on mount (jumping here from a comment). */
    initialAccount?: { companyCode: string; accountCode: string } | null;
    /** Booking to highlight inside the transaction modal. */
    initialTransactionRef?: string | null;
    onClose: () => void;
}

export function BabLineAccountModal({
    line, year, month, apiFilters, signMode, showBuColumn, canDrillCompany, commentCounts, commentedTxRefs,
    onCommentClick, queryCounts, queriedTxRefs, onQueryClick,
    initialAccount, initialTransactionRef, onClose,
}: Props) {
    const [accounts, setAccounts] = useState<BabLineAccount[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sort, setSort] = useState<SortState<SortKey>>({ key: null, direction: 'desc' });
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'bab-line-accounts-col-widths');
    const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
    const [drillAccount, setDrillAccount] = useState<BabLineAccount | null>(null);

    // Only when no booking modal sits on top of this one.
    useTopmostEscape(!drillAccount, onClose);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        babReportApi.getLineAccounts(year, month, line.lineCode, apiFilters)
            .then(data => { if (active) setAccounts(data); })
            .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Failed to load accounts'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [line.lineCode, year, month, apiFilters]);

    const companies = useMemo(() => {
        const set = new Set<string>();
        for (const a of accounts ?? []) set.add(a.companyCode);
        return Array.from(set).sort();
    }, [accounts]);

    // Reset the company filter whenever a new account set arrives.
    useEffect(() => { setSelectedCompanies(new Set(companies)); }, [companies]);

    // Jump straight to a booking comment's account once the rows are there — but never past
    // the lock: this path must not become a way around it.
    useEffect(() => {
        if (!initialAccount || !accounts) return;
        if (!canDrillCompany(initialAccount.companyCode)) return;
        const match = accounts.find(a => a.companyCode === initialAccount.companyCode && a.accountCode === initialAccount.accountCode);
        if (match) setDrillAccount(match);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts]);

    const filtered = useMemo(
        () => (accounts ?? []).filter(a => selectedCompanies.has(a.companyCode)),
        [accounts, selectedCompanies]
    );

    const sorted = useMemo(() => {
        if (!sort.key) return filtered;
        const key = sort.key;
        const dir = sort.direction === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            let cmp = 0;
            switch (key) {
                case 'company': cmp = a.companyCode.localeCompare(b.companyCode); break;
                case 'account': cmp = a.accountCode.localeCompare(b.accountCode, undefined, { numeric: true }); break;
                case 'deltaBudgetPct':
                    cmp = (deltaPct(a.deltaBudget, a.budgetYTD) ?? -Infinity) - (deltaPct(b.deltaBudget, b.budgetYTD) ?? -Infinity);
                    break;
                case 'deltaPYPct':
                    cmp = (deltaPct(a.deltaPY, a.priorYearYTD) ?? -Infinity) - (deltaPct(b.deltaPY, b.priorYearYTD) ?? -Infinity);
                    break;
                default: cmp = a[key] - b[key];
            }
            return cmp * dir;
        });
    }, [filtered, sort]);

    const totals = useMemo(() => filtered.reduce(
        (acc, a) => ({
            actualYTD: acc.actualYTD + a.actualYTD,
            budgetYTD: acc.budgetYTD + a.budgetYTD,
            priorYearYTD: acc.priorYearYTD + a.priorYearYTD,
            deltaBudget: acc.deltaBudget + a.deltaBudget,
            deltaPY: acc.deltaPY + a.deltaPY,
        }),
        { actualYTD: 0, budgetYTD: 0, priorYearYTD: 0, deltaBudget: 0, deltaPY: 0 }
    ), [filtered]);

    // The line's budget comes from BudgetByLineMonth, the accounts' from BudgetByAccount.
    // They can diverge — say so rather than let the reader wonder.
    const budgetGap = Math.abs(totals.budgetYTD - line.budgetYTD);
    const showBudgetNote = accounts !== null && selectedCompanies.size === companies.length && budgetGap > 1;

    // A resize ends with a mouseup in the header cell, which the browser reports as a click —
    // without this guard every drag would also re-sort the column.
    const onSort = (key: SortKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC_KEYS.includes(key)));
    };
    const numCell = 'px-3 py-1.5 text-right tabular-nums';
    // Every account on a line inherits that line's direction, so one signage governs the whole
    // modal. The delta COLOURS below stay on the native values — see getDeltaColorClass.
    const fmt = (amount: number) => formatEur(applySign(amount, line.signage, signMode));

    const toggleCompany = (code: string) => setSelectedCompanies(prev => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code); else next.add(code);
        return next;
    });

    /** An account's badge also carries the comments written on its bookings. */
    const countFor = (a: BabLineAccount): BabCommentCount =>
        commentCounts[accountKey(a.companyCode, a.accountCode)] ?? { total: 0, line: 0, account: 0, booking: 0 };

    const queryCountFor = (a: BabLineAccount): BabQueryCount =>
        queryCounts[accountKey(a.companyCode, a.accountCode)] ?? { total: 0, open: 0, overdue: 0 };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
                <div className="bg-white rounded-lg shadow-xl w-[96vw] max-w-[1700px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {line.lineName} <span className="text-gray-400 text-sm">({line.lineCode})</span>
                            </h3>
                            <p className="text-sm text-gray-500">
                                Accounts for {year}-{String(month).padStart(2, '0')} (YTD) · all amounts in EUR
                            </p>
                        </div>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {companies.length > 1 && (
                        <div className="flex flex-wrap items-center gap-1.5 px-6 py-2 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs font-medium text-gray-500 mr-1">Company</span>
                            {companies.map(code => (
                                <button
                                    key={code}
                                    onClick={() => toggleCompany(code)}
                                    className={`px-2 py-0.5 rounded text-xs font-medium uppercase border ${selectedCompanies.has(code)
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100'}`}
                                    title={formatCompanyLabel(code)}
                                >
                                    {code}
                                </button>
                            ))}
                            <button onClick={() => setSelectedCompanies(new Set(companies))} className="ml-2 text-xs text-blue-600 hover:underline">All</button>
                            <button onClick={() => setSelectedCompanies(new Set())} className="text-xs text-blue-600 hover:underline">None</button>
                        </div>
                    )}

                    <div className="flex-1 overflow-auto p-6">
                        {loading && (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                <span className="ml-2 text-gray-500">Loading accounts...</span>
                            </div>
                        )}
                        {error && <p className="text-red-600 text-center py-12">{error}</p>}

                        {!loading && !error && accounts && (
                            accounts.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No account detail available for this line.</p>
                            ) : (
                                <div>
                                    {/* Account names are long (dpt spells out the licence plate) — one row,
                                        one line, clipped with the full text in `title`; drag a column edge
                                        to see more.

                                        Deliberately NOT `overflow-x-auto`: that would make this div the
                                        nearest scrolling ancestor of the sticky <th>, and being unbounded
                                        in height it never scrolls vertically — so the header would ride
                                        out of view. Both axes belong to the enclosing
                                        `flex-1 overflow-auto`, which `max-h-[85vh]` bounds. */}
                                    <table
                                        className="w-full table-fixed text-sm border-collapse [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap"
                                        style={{ minWidth: totalWidthOf(COLUMNS.map(c => c.key)) + 2 * BADGE_COL_WIDTH }}
                                    >
                                        <colgroup>
                                            {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                                            <col style={{ width: BADGE_COL_WIDTH }} />
                                            <col style={{ width: BADGE_COL_WIDTH }} />
                                        </colgroup>
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                {COLUMNS.map(c => (
                                                    <SortableHeader
                                                        key={c.key}
                                                        label={c.label}
                                                        sortKey={c.key}
                                                        sort={sort}
                                                        onSort={onSort}
                                                        align={c.align}
                                                        isResizing={resizingKey === c.key}
                                                        onResizeStart={x => startResize(c.key, x)}
                                                        onResetWidth={() => resetWidth(c.key)}
                                                    />
                                                ))}
                                                <th className="sticky top-0 z-10 bg-gray-50 px-1 py-2"> </th>
                                                <th className="sticky top-0 z-10 bg-gray-50 px-1 py-2"> </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            <tr className="bg-gray-100 font-semibold border-b-2 border-gray-300">
                                                <td className="px-3 py-1.5" colSpan={2}>Total</td>
                                                <td className={numCell}>{fmt(totals.actualYTD)}</td>
                                                <td className={numCell}>{fmt(totals.budgetYTD)}</td>
                                                <td className={`${numCell} ${getDeltaColorClass(totals.deltaBudget, line.signage)}`}>{fmt(totals.deltaBudget)}</td>
                                                <td className={`${numCell} ${getDeltaColorClass(totals.deltaBudget, line.signage)}`}>{formatPercent(deltaPct(totals.deltaBudget, totals.budgetYTD))}</td>
                                                <td className={numCell}>{fmt(totals.priorYearYTD)}</td>
                                                <td className={`${numCell} ${getDeltaColorClass(totals.deltaPY, line.signage)}`}>{fmt(totals.deltaPY)}</td>
                                                <td className={`${numCell} ${getDeltaColorClass(totals.deltaPY, line.signage)}`}>{formatPercent(deltaPct(totals.deltaPY, totals.priorYearYTD))}</td>
                                                <td />
                                                <td />
                                            </tr>

                                            {sorted.length === 0 ? (
                                                <tr>
                                                    <td colSpan={11} className="px-3 py-6 text-center text-gray-500">
                                                        No accounts match the selected company filter.
                                                    </td>
                                                </tr>
                                            ) : sorted.map(a => {
                                                const count = countFor(a);
                                                const canDrill = canDrillCompany(a.companyCode);
                                                return (
                                                    <tr
                                                        key={`${a.companyCode}-${a.buCode}-${a.accountCode}`}
                                                        className={canDrill ? 'hover:bg-blue-50 cursor-pointer' : undefined}
                                                        onClick={canDrill ? () => setDrillAccount(a) : undefined}
                                                        title={canDrill
                                                            ? 'Show the individual bookings'
                                                            : `Individual bookings of ${a.companyCode.toUpperCase()} are restricted. The totals here are shown in full — ask an admin for access to the bookings.`}
                                                    >
                                                        <td className="px-3 py-1.5">
                                                            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 uppercase" title={formatCompanyLabel(a.companyCode)}>
                                                                {a.companyCode}
                                                            </span>
                                                            {!canDrill && <Lock className="ml-1 inline h-3 w-3 text-gray-400" />}
                                                            {showBuColumn && <span className="ml-1 text-xs text-gray-400">{a.buCode}</span>}
                                                        </td>
                                                        <td
                                                            className="px-3 py-1.5 truncate"
                                                            title={a.accountName ? `${a.accountCode} — ${a.accountName}` : a.accountCode}
                                                        >
                                                            <span className="font-medium">{a.accountCode}</span>
                                                            {a.accountName && <span className="text-gray-500"> — {a.accountName}</span>}
                                                        </td>
                                                        <td className={numCell}>{fmt(a.actualYTD)}</td>
                                                        <td className={`${numCell} text-gray-600`}>{fmt(a.budgetYTD)}</td>
                                                        <td className={`${numCell} ${getDeltaColorClass(a.deltaBudget, line.signage)}`}>{fmt(a.deltaBudget)}</td>
                                                        <td className={`${numCell} ${getDeltaColorClass(a.deltaBudget, line.signage)}`}>{formatPercent(deltaPct(a.deltaBudget, a.budgetYTD))}</td>
                                                        <td className={`${numCell} text-gray-600`}>{fmt(a.priorYearYTD)}</td>
                                                        <td className={`${numCell} ${getDeltaColorClass(a.deltaPY, line.signage)}`}>{fmt(a.deltaPY)}</td>
                                                        <td className={`${numCell} ${getDeltaColorClass(a.deltaPY, line.signage)}`}>{formatPercent(deltaPct(a.deltaPY, a.priorYearYTD))}</td>
                                                        <td className="px-1 py-1.5 text-center w-10">
                                                            <button
                                                                type="button"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    onCommentClick({
                                                                        level: 'account',
                                                                        lineCode: line.lineCode,
                                                                        lineName: line.lineName,
                                                                        companyCode: a.companyCode,
                                                                        buCode: a.buCode,
                                                                        accountCode: a.accountCode,
                                                                        accountName: a.accountName,
                                                                    });
                                                                }}
                                                                className="relative inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                                                                title={count.total > 0
                                                                    ? `${count.total} comment(s)${count.booking > 0 ? `, ${count.booking} on bookings` : ''}`
                                                                    : 'Add a comment on this account'}
                                                            >
                                                                <MessageSquare size={15} className={count.total > 0 ? 'text-blue-600' : 'text-gray-400'} />
                                                                {count.total > 0 && (
                                                                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-blue-600 text-white text-[9px] leading-[14px] font-semibold">
                                                                        {count.total}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        </td>
                                                        <td className="px-1 py-1.5 text-center w-10">
                                                            <button
                                                                type="button"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    onQueryClick({
                                                                        level: 'account',
                                                                        lineCode: line.lineCode,
                                                                        lineName: line.lineName,
                                                                        companyCode: a.companyCode,
                                                                        buCode: a.buCode,
                                                                        accountCode: a.accountCode,
                                                                        accountName: a.accountName,
                                                                    });
                                                                }}
                                                                className="relative inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                                                                title={queryCountFor(a).total > 0
                                                                    ? `${queryCountFor(a).total} quer${queryCountFor(a).total === 1 ? 'y' : 'ies'}${queryCountFor(a).overdue > 0 ? `, ${queryCountFor(a).overdue} overdue` : ''}`
                                                                    : 'Ask a question about this account'}
                                                            >
                                                                <HelpCircle
                                                                    size={15}
                                                                    className={queryCountFor(a).total > 0
                                                                        ? (queryCountFor(a).overdue > 0 ? 'text-red-600' : queryCountFor(a).open > 0 ? 'text-amber-600' : 'text-gray-500')
                                                                        : 'text-gray-400'}
                                                                />
                                                                {queryCountFor(a).total > 0 && (
                                                                    <span className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full text-white text-[9px] leading-[14px] font-semibold ${
                                                                        queryCountFor(a).overdue > 0 ? 'bg-red-600'
                                                                            : queryCountFor(a).open > 0 ? 'bg-amber-500' : 'bg-gray-400'
                                                                    }`}>
                                                                        {queryCountFor(a).total}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    <div className="mt-2 flex items-start justify-between gap-4">
                                        <p className="text-xs text-gray-500">
                                            Accounts ({filtered.length}{filtered.length !== accounts.length ? ` of ${accounts.length}` : ''}) ·{' '}
                                            {filtered.every(a => canDrillCompany(a.companyCode))
                                                ? 'click a row for the individual bookings'
                                                : 'click a row for the individual bookings — locked rows are not available to you'}
                                        </p>
                                        {showBudgetNote && (
                                            <p className="text-xs text-amber-700 flex items-center gap-1 text-right">
                                                <Info className="h-3.5 w-3.5 flex-shrink-0" />
                                                Budget per account differs from the line budget by {formatEur(budgetGap)} EUR
                                                (line: BudgetByLineMonth, accounts: BudgetByAccount).
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            {drillAccount && (
                <BabAccountTransactionModal
                    line={line}
                    account={drillAccount}
                    year={year}
                    month={month}
                    signMode={signMode}
                    commentedTxRefs={commentedTxRefs}
                    onCommentClick={onCommentClick}
                    queriedTxRefs={queriedTxRefs}
                    onQueryClick={onQueryClick}
                    highlightTransactionRef={initialTransactionRef ?? null}
                    onClose={() => setDrillAccount(null)}
                />
            )}
        </>
    );
}
